import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Check, Plug, RefreshCw } from 'lucide-react'
import { Alert, Badge, Button, Card, CardBody, SkeletonList, Tabs } from '@/components/ui'
import { PageHeader, QueryBoundary } from '@/components/domain'
import { api } from '@/internationalization/api-client'
import { qk } from '@/internationalization/query-keys'
import { formatRelative } from '@/internationalization/format'
import { useT } from '@/internationalization/i18n'
import { toast } from '@/stores'
import { useState } from 'react'
import type { Integration, IntegrationStatus } from '@/types'

type IntegrationConnectResult = Integration | { authorizationUrl: string }

const CATEGORY_KEY: Record<Integration['category'], string> = {
  calendar: 'integrations.category.calendar',
  conferencing: 'integrations.category.conferencing',
  productivity: 'integrations.category.productivity',
  crm: 'integrations.category.crm',
  storage: 'integrations.category.storage',
  chat: 'integrations.category.chat',
}

// Only these four are exposed in the UI for now. Every other integration keeps
// its card, connect flow and backend intact -- add an id back to show it again.
const VISIBLE_INTEGRATIONS = new Set(['microsoft-teams', 'slack', 'jira', 'asana'])

const STATUS_BADGE: Record<IntegrationStatus, { key: string; tone: 'success' | 'neutral' | 'danger' | 'warning' }> = {
  connected: { key: 'integrations.status.connected', tone: 'success' },
  disconnected: { key: 'integrations.status.disconnected', tone: 'neutral' },
  error: { key: 'integrations.status.error', tone: 'danger' },
  pending_consent: { key: 'integrations.status.pending', tone: 'warning' },
}

/**
 * Connect the calendars the recorder discovers meetings from, and the tools
 * summaries and action items are pushed into.
 *
 * A broken connection is surfaced at the top of the page, not just as a badge
 * on its own card: a token that expired two weeks ago has been silently
 * dropping action items for two weeks, and the badge is easy to scroll past.
 */
export function IntegrationsPage() {
  const t = useT()
  const [category, setCategory] = useState<'all' | Integration['category']>('all')
  const queryClient = useQueryClient()
  const { data, isPending, error, refetch } = useQuery({
    queryKey: qk.integrations,
    queryFn: () => api.get<Integration[]>('/integrations'),
  })

  const connect = useMutation({
    // Two literal paths rather than one interpolated verb: an endpoint built by
    // string concatenation cannot be checked against the spec.
    mutationFn: ({ id, action }: { id: string; action: 'connect' | 'disconnect' }) =>
      action === 'connect'
        ? api.post<IntegrationConnectResult>(`/integrations/${id}/connect`)
        : api.post<IntegrationConnectResult>(`/integrations/${id}/disconnect`),
    onSuccess: (result, variables) => {
      if ('authorizationUrl' in result) {
        window.location.assign(result.authorizationUrl)
        return
      }
      queryClient.invalidateQueries({ queryKey: qk.integrations })
      toast.success(variables.action === 'connect' ? t('integrations.toast.connected') : t('integrations.toast.disconnected'))
    },
    onError: () => toast.error(t('integrations.toast.failTitle'), t('integrations.toast.failBody')),
  })

  const visible = data?.filter((item) => VISIBLE_INTEGRATIONS.has(item.id)) ?? []
  const broken = visible.filter((item) => item.status === 'error')
  const shown = visible.filter((item) => category === 'all' || item.category === category)

  return (
    <div>
      <PageHeader
        title={t('integrations.title')}
        description={t('integrations.description')}
        below={
          <Tabs
            variant="pill"
            activeId={category}
            onChange={(id) => setCategory(id as typeof category)}
            tabs={[
              { id: 'all', label: t('integrations.category.all') },
              ...Object.entries(CATEGORY_KEY).map(([id, key]) => ({ id, label: t(key) })),
            ]}
          />
        }
      />

      {broken.length > 0 && (
        <Alert
          tone="danger"
          title={
            broken.length > 1
              ? t('integrations.needAttentionPlural', { count: broken.length })
              : t('integrations.needAttention', { count: broken.length })
          }
          className="mb-4"
          action={
            <Button
              size="sm"
              leftIcon={<RefreshCw className="h-4 w-4" />}
              onClick={() => connect.mutate({ id: broken[0].id, action: 'connect' })}
            >
              {t('common.reconnect')}
            </Button>
          }
        >
          {broken.map((item) => item.detail ?? item.name).join(' · ')}
        </Alert>
      )}

      <QueryBoundary
        isLoading={isPending}
        error={error}
        onRetry={refetch}
        skeleton={<SkeletonList rows={6} className="sb-panel p-4" />}
      >
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {shown.map((item) => {
            const badge = STATUS_BADGE[item.status]
            return (
              <Card key={item.id} className="flex h-full flex-col">
                <CardBody className="flex flex-1 flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-surface-muted text-fg-muted">
                      <Plug aria-hidden className="h-4 w-4" />
                    </span>
                    <Badge tone={badge.tone}>{t(badge.key)}</Badge>
                  </div>
                  <h3 className="mt-3 text-md font-semibold text-fg">{item.name}</h3>
                  <p className="mt-1 flex-1 text-sm text-fg-muted">{item.description}</p>

                  {item.status === 'error' && item.detail && (
                    <p className="mt-2 flex items-start gap-1.5 text-sm text-danger">
                      <AlertTriangle aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      {item.detail}
                    </p>
                  )}
                  {item.status === 'connected' && item.connectedAt && (
                    <p className="mt-2 text-xs text-fg-subtle">
                      {t('integrations.connectedBy', { who: item.connectedBy ?? '—', when: formatRelative(item.connectedAt) })}
                    </p>
                  )}

                  <div className="mt-3 flex items-center gap-2">
                    {item.status === 'connected' ? (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          leftIcon={<Check className="h-4 w-4" />}
                          disabled
                        >
                          {t('common.active')}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => connect.mutate({ id: item.id, action: 'disconnect' })}
                        >
                          {t('common.disconnect')}
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        variant="primary"
                        isLoading={connect.isPending && connect.variables?.id === item.id}
                        onClick={() => connect.mutate({ id: item.id, action: 'connect' })}
                      >
                        {item.status === 'error' ? t('common.reconnect') : t('common.connect')}
                      </Button>
                    )}
                  </div>
                </CardBody>
              </Card>
            )
          })}
        </div>
      </QueryBoundary>
    </div>
  )
}
